import React from "react";
import PropTypes from "prop-types";
import {
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  MenuItem,
  InputAdornment,
  Box,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";

/**
 * CommonSelect Component
 * Reusable select / dropdown component adhering to AutoBET design tokens.
 * Visually consistent with CommonInput (background, borders, shadows, focus glow, sizing).
 */
const CommonSelect = ({
  label,
  value,
  defaultValue,
  onChange,
  placeholder,
  options = [],
  children,
  size = "small",
  fullWidth = true,
  width,
  startAdornment,
  endAdornment,
  disabled = false,
  readOnly = false,
  error = false,
  helperText,
  displayEmpty = true,
  SelectProps = {},
  MenuProps = {},
  inputProps = {},
  sx = {},
  formControlSx = {},
  id,
  name,
  renderValue,
  ...props
}) => {
  const resolvedStartAdornment = startAdornment
    ? React.isValidElement(startAdornment) && startAdornment.type === InputAdornment
      ? startAdornment
      : <InputAdornment position="start">{startAdornment}</InputAdornment>
    : undefined;

  const resolvedEndAdornment = endAdornment
    ? React.isValidElement(endAdornment) && endAdornment.type === InputAdornment
      ? endAdornment
      : <InputAdornment position="end">{endAdornment}</InputAdornment>
    : undefined;

  const resolvedWidth = width || (fullWidth ? "100%" : undefined);

  const mergedMenuProps = {
    disableScrollLock: true,
    PaperProps: {
      onWheel: (e) => {
        e.stopPropagation();
      },
      sx: {
        borderRadius: borderRadiusPx.xl,
        boxShadow: customShadows.dropdownMenu || "0 10px 30px rgba(0, 0, 0, 0.12)",
        border: `1px solid ${colors.neutral.border}`,
        backgroundColor: colors.background.paper,
        mt: 0.75,
        p: 0.5,
        maxHeight: 280,
        overflowY: "auto",
        overscrollBehavior: "contain",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: colors.slate[300],
          borderRadius: borderRadiusPx.xs,
        },
        "& .MuiMenuItem-root": {
          fontSize: "14px",
          fontWeight: 500,
          color: colors.slate[700],
          py: 1,
          px: 1.5,
          my: 0.25,
          borderRadius: borderRadiusPx.md,
          transition: "all 0.15s ease-in-out",
          "&:hover": {
            backgroundColor: colors.slate[100],
            color: colors.slate[900],
          },
          "&.Mui-selected": {
            backgroundColor: colors.blue[50],
            color: colors.primary.main,
            fontWeight: 600,
            "&:hover": {
              backgroundColor: colors.blue[100],
            },
          },
          "&.Mui-disabled": {
            opacity: 0.5,
          },
        },
      },
    },
    MenuListProps: {
      onWheel: (e) => {
        e.stopPropagation();
      },
      sx: {
        overscrollBehavior: "contain",
        py: 0.5,
      },
    },
    ...MenuProps,
    ...(SelectProps?.MenuProps || {}),
  };

  const defaultRenderValue = (selected) => {
    if (
      selected === "" ||
      selected === undefined ||
      selected === null ||
      (Array.isArray(selected) && selected.length === 0)
    ) {
      if (placeholder) {
        return (
          <Box component="span" sx={{ color: colors.slate[400], fontWeight: 400 }}>
            {placeholder}
          </Box>
        );
      }
      return "";
    }

    if (options && options.length > 0) {
      const match = options.find((opt) =>
        typeof opt === "object" ? String(opt.value) === String(selected) : String(opt) === String(selected)
      );
      if (match) {
        return typeof match === "object" ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {match.icon && (
              <Box component="span" sx={{ display: "flex", alignItems: "center" }}>
                {match.icon}
              </Box>
            )}
            {match.label}
          </Box>
        ) : (
          match
        );
      }
    }

    return selected;
  };

  const selectElement = (
    <Select
      id={id}
      name={name}
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      displayEmpty={displayEmpty || Boolean(placeholder)}
      disabled={disabled}
      readOnly={readOnly}
      error={error}
      size={size}
      fullWidth={fullWidth}
      startAdornment={resolvedStartAdornment}
      endAdornment={resolvedEndAdornment}
      IconComponent={KeyboardArrowDownRoundedIcon}
      renderValue={renderValue || (placeholder ? defaultRenderValue : undefined)}
      MenuProps={mergedMenuProps}
      inputProps={{
        ...inputProps,
        ...(SelectProps?.inputProps || {}),
      }}
      sx={{
        bgcolor: disabled ? colors.slate[100] : colors.background.paper,
        borderRadius: borderRadiusPx.xl,
        ...(resolvedWidth ? { width: resolvedWidth } : {}),
        boxShadow: customShadows.cardFlat,
        transition: "all 0.2s ease-in-out",
        border: `1px solid ${error ? colors.error?.main || "#ef4444" : colors.neutral.border}`,
        "& .MuiOutlinedInput-notchedOutline": {
          display: "none",
        },
        "&:hover": {
          borderColor: error
            ? colors.error?.dark || "#dc2626"
            : colors.neutral.borderHover || colors.slate[300],
        },
        "&.Mui-focused": {
          borderColor: error
            ? colors.error?.main || "#ef4444"
            : colors.primary.main,
          boxShadow: error
            ? "0 4px 12px rgba(239, 68, 68, 0.15)"
            : `0 4px 12px ${colors.primary.subtle}`,
        },
        "& .MuiSelect-select": {
          py: size === "small" ? 1.25 : 1.5,
          pl: startAdornment ? 1 : 1.75,
          pr: "36px !important",
          fontSize: "14px",
          fontWeight: 500,
          color: colors.text.primary,
          display: "flex",
          alignItems: "center",
          minHeight: "auto !important",
          boxSizing: "border-box",
          border: "none !important",
          outline: "none !important",
          boxShadow: "none !important",
          background: "transparent !important",
          "&:focus": {
            backgroundColor: "transparent !important",
            border: "none !important",
            outline: "none !important",
            boxShadow: "none !important",
          },
        },
        "& .MuiSelect-icon": {
          color: colors.slate[400],
          right: 12,
          fontSize: "20px",
          transition: "transform 0.2s ease-in-out, color 0.2s ease-in-out",
        },
        "& .MuiSelect-iconOpen": {
          transform: "rotate(180deg)",
          color: colors.primary.main,
        },
        "&:hover .MuiSelect-icon": {
          color: colors.slate[600],
        },
        ...sx,
      }}
      {...SelectProps}
      {...props}
    >
      {options && options.length > 0
        ? options.map((option, idx) => {
            const optValue = typeof option === "object" ? option.value : option;
            const optLabel = typeof option === "object" ? option.label : option;
            const optDisabled = typeof option === "object" ? option.disabled : false;
            const optIcon = typeof option === "object" ? option.icon : null;
            return (
              <MenuItem
                key={optValue !== undefined ? optValue : `opt-${idx}`}
                value={optValue}
                disabled={optDisabled}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                {optIcon && (
                  <Box component="span" sx={{ display: "flex", alignItems: "center", mr: 0.5 }}>
                    {optIcon}
                  </Box>
                )}
                {optLabel}
              </MenuItem>
            );
          })
        : children}
    </Select>
  );

  if (label || helperText) {
    return (
      <FormControl
        fullWidth={fullWidth}
        error={error}
        disabled={disabled}
        sx={{
          ...(resolvedWidth ? { width: resolvedWidth } : {}),
          ...formControlSx,
        }}
      >
        {label && (
          <FormLabel
            htmlFor={id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.5,
              fontWeight: 600,
              fontSize: "14px",
              color: `${colors.slate[700]} !important`,
              "&.Mui-focused": {
                color: `${colors.slate[700]} !important`,
              },
            }}
          >
            {label}
          </FormLabel>
        )}
        {selectElement}
        {helperText && (
          <FormHelperText
            sx={{
              fontSize: "12px",
              mt: 0.5,
              mx: 0.5,
              color: error ? colors.error?.main || "#ef4444" : colors.slate[500],
            }}
          >
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  return selectElement;
};

CommonSelect.propTypes = {
  label: PropTypes.node,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        value: PropTypes.any.isRequired,
        label: PropTypes.node.isRequired,
        disabled: PropTypes.bool,
        icon: PropTypes.node,
      }),
    ])
  ),
  children: PropTypes.node,
  size: PropTypes.oneOf(["small", "medium"]),
  fullWidth: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  displayEmpty: PropTypes.bool,
  SelectProps: PropTypes.object,
  MenuProps: PropTypes.object,
  inputProps: PropTypes.object,
  sx: PropTypes.object,
  formControlSx: PropTypes.object,
  id: PropTypes.string,
  name: PropTypes.string,
  renderValue: PropTypes.func,
};

export default CommonSelect;
